"use client";

import { c as _c } from "react/compiler-runtime";
import { IconFileText } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconBookOpenOutline18, IconCheckOutline18, IconChevronRightOutline18 } from "nucleo-ui-outline-18";
import ResponsiveModal from "@/components/custom/ResponsiveModal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { RadioGroup } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { MemoizedMarkdown } from "@/components/ui/MemoizedMarkdown";
import { fetchPushbulletDevicesFromApi, upsertPushbulletDevice } from "@/lib/pushbullet";
import { showToast } from "@/lib/toast";
import { useConnectorsStore, type Connector, type ConnectorType } from "@/stores/connectorsStore";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { useState } from "react";
import { ConnectorLogo } from "./ConnectorLogo";
import { ConnectorOptionRow } from "./ConnectorOptionRow";
import { SmsPhoneVerifyInput } from "./SmsPhoneVerifyInput";
import { FarcasterConnectButton } from "./FarcasterConnectButton";
import { SlackOAuthConnectButton } from "./SlackOAuthConnectButton";
import { usePipedreamClient } from "./usePipedreamClient";
const PRIMARY_GRADIENT_BUTTON_CLASS = "flex h-10 items-center gap-1 rounded-ds-md bg-gradient-to-b from-ds-brand-500 to-ds-brand-600 px-6 text-sm font-medium text-white shadow-ds-brand-glossy transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-50";
type TelegramMode = "b3os" | "custom";
const TELEGRAM_DEFAULT_NAMES: Record<TelegramMode, string> = {
  b3os: "B3OS Bot",
  custom: "My Telegram Bot"
};
const PUSHOVER_DEFAULT_NAME = "B3OS Pushover";
function getInitialTelegramName(connector: Connector | undefined, preselectedType: string | undefined, initialTelegramMode: TelegramMode | undefined): string {
  if (connector?.name) return connector.name;
  if (preselectedType !== "telegram-bot") return "";
  const mode = initialTelegramMode ?? "b3os";
  return TELEGRAM_DEFAULT_NAMES[mode];
}
function getInitialTelegramData(connector: Connector | undefined, preselectedType: string | undefined, initialTelegramMode
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- form data shape varies by connector schema
: TelegramMode | undefined): Record<string, any> {
  if (connector?.data) return connector.data;
  if (preselectedType === "pushover") return {};
  if (preselectedType !== "telegram-bot") return {};
  return {
    mode: initialTelegramMode ?? "b3os"
  };
}
function getInitialConnectorName(connector: Connector | undefined, preselectedType: string | undefined, initialTelegramMode: TelegramMode | undefined): string {
  if (connector?.name) return connector.name;
  if (preselectedType === "pushover") return PUSHOVER_DEFAULT_NAME;
  return getInitialTelegramName(connector, preselectedType, initialTelegramMode);
}
interface SchemaProperty {
  title?: string;
  description?: string;
  format?: string;
  pattern?: string;
  uiHidden?: boolean;
  enum?: string[];
  default?: string;
}
interface ConnectorFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  connectorTypes: ConnectorType[];
  connector?: Connector;
  preselectedType?: string;
  initialTelegramMode?: TelegramMode;
}
export default function ConnectorFormModal({
  isOpen,
  onClose,
  connectorTypes,
  connector,
  preselectedType,
  initialTelegramMode
}: ConnectorFormModalProps) {
  const createConnector = useConnectorsStore(state => state.createConnector);
  const updateConnector = useConnectorsStore(state_0 => state_0.updateConnector);
  const loadConnectors = useConnectorsStore(state_1 => state_1.loadConnectors);
  const [selectedType, setSelectedType] = useState<string>(connector?.type || preselectedType || "");
  const [name, setName] = useState(() => getInitialConnectorName(connector, preselectedType, initialTelegramMode));
  const [description, setDescription] = useState(connector?.description || "");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- form data shape varies by connector schema
  const [data, setData] = useState<Record<string, any>>(() => getInitialTelegramData(connector, preselectedType, initialTelegramMode));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    isConnecting: isPipedreamConnecting,
    error: pipedreamError,
    clearError: clearPipedreamError,
    connectAccount
  } = usePipedreamClient();
  const isEditing = !!connector;
  const hasPreselectedType = !!preselectedType;
  const [nameManuallyEdited, setNameManuallyEdited] = useState(!!connector);

  // Get the schema for the selected type
  const selectedConnectorType = connectorTypes.find(ct => ct.type === selectedType);
  const dataSchema = selectedConnectorType?.dataSchema;

  // Initialize form when a new connector type is selected
  const handleTypeChange = (newType: string) => {
    setSelectedType(newType);

    // Initialize data fields for the new type
    if (newType && !connector) {
      const connectorType = connectorTypes.find(ct_0 => ct_0.type === newType);
      const schema = connectorType?.dataSchema;
      if (schema?.properties) {
        const initialData: Record<string, string | number | boolean> = {};
        Object.keys(schema.properties).forEach(key => {
          initialData[key] = "";
        });
        setData(initialData);
      } else {
        setData({});
      }
      // Set default name
      if (connectorType) {
        setName(`${connectorType.name} Connector`);
      }
    }
  };
  const handleFieldChange = (fieldName: string, value: string) => {
    setData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    if (selectedType === "sms" && fieldName === "number" && value && !nameManuallyEdited) {
      setName(`iMessage & SMS ${value}`);
    }
  };
  const handleSmsPhoneTyping = (phoneNumber: string) => {
    if (!nameManuallyEdited) {
      setName(`iMessage & SMS ${phoneNumber}`);
    }
  };
  const handleFarcasterConnect = (farcasterData: {
    fid: number;
    signerUuid: string;
    username: string;
  }) => {
    setData(prev_0 => ({
      ...prev_0,
      fid: farcasterData.fid,
      // Keep as integer
      signerUuid: farcasterData.signerUuid
    }));
    // Auto-set name if not already set
    if (!name) {
      setName(`Farcaster - @${farcasterData.username}`);
    }
  };
  const handleFarcasterDisconnect = () => {
    // Clear Farcaster-specific data from form
    setData(prev_1 => {
      const {
        fid,
        signerUuid,
        ...rest
      } = prev_1;
      return rest;
    });
    // Clear auto-generated name if it looks like a Farcaster name
    if (name.startsWith("Farcaster - @")) {
      setName("");
    }
  };
  const handlePipedreamConnect = (pipedreamData: {
    accountId: string;
    appSlug: string;
    appName: string;
    accountName: string;
  }) => {
    setData(prev_2 => ({
      ...prev_2,
      accountId: pipedreamData.accountId
    }));
    // Auto-set name if not already set or is the default connector name
    const defaultConnectorName = selectedConnectorType ? `${selectedConnectorType.name} Connector` : "";
    if (!name || name === defaultConnectorName) {
      setName(pipedreamData.accountName);
    }
  };
  const handlePipedreamDisconnect = () => {
    // Clear Pipedream-specific data from form
    setData(prev_3 => {
      const {
        accountId,
        appSlug,
        ...rest_0
      } = prev_3;
      return rest_0;
    });
    // Reset to default connector name
    if (selectedConnectorType) {
      setName(`${selectedConnectorType.name} Connector`);
    } else {
      setName("");
    }
  };

  // Switch the Telegram bot selector inline (used when the modal is opened from
  // /connectors without an explicit initialTelegramMode). Replacing `data`
  // entirely also clears any partially-typed botToken when leaving custom mode.
  const handleSelectTelegramMode = (mode: TelegramMode) => {
    setData({
      mode
    });
    const otherMode: TelegramMode = mode === "b3os" ? "custom" : "b3os";
    if (!name || name === TELEGRAM_DEFAULT_NAMES[otherMode]) {
      setName(TELEGRAM_DEFAULT_NAMES[mode]);
    }
  };
  const isFarcasterType = selectedType === "farcaster";
  // Detect Pipedream connectors by provider field (e.g., pipedream:gmail, pipedream:twilio)
  const isPipedreamType = selectedConnectorType?.provider === "pipedream";
  // Detect native OAuth connectors (e.g., Slack with provider "native")
  const isNativeOAuthType = selectedConnectorType?.provider === "native";
  const isTelegramBotType = selectedType === "telegram-bot";
  // When editing, data.mode may be empty since the API returns maskedData, not data.
  const telegramMode = data.mode as string || (isEditing ? connector?.maskedData?.mode as string : undefined) || "b3os";
  const isB3osBot = isTelegramBotType && telegramMode === "b3os";
  // Show the inline B3OS Bot / Advanced selector when the modal is opened directly
  // from /connectors (no caller-supplied mode). The workflow sidebar already renders
  // its own radio above the modal, so it sets initialTelegramMode and we skip ours.
  const showTelegramModeSelector = isTelegramBotType && !isEditing && initialTelegramMode === undefined;
  const isPushoverType = selectedType === "pushover";
  const hasConnectedExternalAccount = isNativeOAuthType ? false // Native OAuth connectors are created via backend callback, not via the form
  : isPipedreamType ? Boolean(data.accountId) : isFarcasterType ? Boolean(data.fid && data.signerUuid) : true;
  const hasConnectorName = name.trim().length > 0;
  const hasRequiredFields = !dataSchema?.required?.length || dataSchema.required.every((f: string) => data[f] && String(data[f]).trim() !== "");
  const showSubmitButton = isEditing || hasConnectedExternalAccount;
  const handlePipedreamFooterConnect = async () => {
    const appSlug_0 = selectedConnectorType?.providerConfig?.appSlug;
    const appName = selectedConnectorType?.name || "App";
    if (!appSlug_0) {
      showToast.error("This connector is missing its Pipedream app configuration");
      return;
    }
    clearPipedreamError();
    const result = await connectAccount({
      appSlug: appSlug_0
    });
    if (!result) {
      return;
    }
    handlePipedreamConnect({
      accountId: result.accountId,
      appSlug: result.appSlug,
      appName,
      accountName: `${appName} Account`
    });
  };

  // OAuth creates the connector server-side without description; patch it after popup closes.
  const patchNewConnectorWithDescription = async (before: Connector[]) => {
    const trimmed = description.trim();
    if (!trimmed) return;
    const beforeIds = new Set(before.map(c => c.id));
    const after = useConnectorsStore.getState().connectors;
    const created = after.find(c_0 => !beforeIds.has(c_0.id) && c_0.type === selectedType);
    if (!created) return;
    try {
      await updateConnector(created.id, {
        description: trimmed
      });
    } catch (err) {
      console.error("Failed to persist connector description:", err);
      // Don't block the flow — connection itself succeeded.
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      showToast.error("Please enter a connector name");
      return;
    }
    if (!selectedType && !isEditing) {
      showToast.error("Please select a connector type");
      return;
    }

    // Validate custom Telegram bot token
    if (isTelegramBotType && telegramMode === "custom" && !data.botToken?.trim()) {
      showToast.error("Bot token is required for custom bot");
      return;
    }

    // Validate required fields
    if (dataSchema?.required) {
      for (const field of dataSchema.required) {
        if (!data[field] || data[field].toString().trim() === "") {
          showToast.error(`${field} is required`);
          return;
        }
      }
    }

    // Inject mode for telegram-bot connectors from the UI selector.
    // Pushover always uses the shared B3OS app — the resolver injects the
    // token server-side, so the connector row stores no secret.
    let submitData = data;
    if (isTelegramBotType) {
      submitData = {
        ...data,
        mode: telegramMode
      };
    } else if (isPushoverType) {
      submitData = {};
    }
    setIsSubmitting(true);
    try {
      if (isEditing) {
        await updateConnector(connector.id, {
          name,
          description,
          data: submitData
        });
        showToast.success("Connector updated successfully");
      } else {
        const created_0 = await createConnector({
          type: selectedType,
          name,
          description,
          data: submitData
        });
        showToast.success("Connector created successfully");

        // Pushbullet: auto-import the user's devices using the access token
        // that's still in plaintext form state. Server never decrypts the
        // connector data to fetch the device list — the frontend does it
        // here once at create time. Best-effort: errors surface a toast but
        // don't block connector creation.
        if (selectedType === "pushbullet" && typeof submitData.accessToken === "string") {
          try {
            const devices = await fetchPushbulletDevicesFromApi(submitData.accessToken);
            await Promise.all(devices.map(d => upsertPushbulletDevice({
              connectorId: created_0.id,
              deviceIden: d.iden,
              nickname: d.nickname,
              type: d.type
            }).catch(() => undefined)));
            if (devices.length > 0) {
              showToast.success(`Imported ${devices.length} Pushbullet device${devices.length === 1 ? "" : "s"}`);
            }
          } catch (err_0) {
            showToast.error(err_0 instanceof Error ? `Couldn't import Pushbullet devices: ${err_0.message}` : "Couldn't import Pushbullet devices");
          }
        }
      }
      onClose();
    } catch (error) {
      showToast.error(error instanceof Error ? error.message : "Failed to save connector");
    } finally {
      setIsSubmitting(false);
    }
  };
  const modalTitle = isEditing ? "Edit Connector" : hasPreselectedType ? `Connect ${selectedConnectorType?.name || "Connector"}` : "Create Connector";
  return <ResponsiveModal open={isOpen} onOpenChange={onClose} title={modalTitle} contentClass="max-w-2xl" innerClass="md:min-h-[calc(85vh-12px)] md:max-h-[calc(85vh-12px)] md:flex md:flex-col md:!p-0 md:!gap-0" preventCloseOnOutsideClick={isPipedreamType}>
      <div className="flex flex-1 flex-col overflow-y-auto">
        {/* Integration logos */}
        <div className="flex items-center justify-center gap-4 pt-8">
          <div className="ds-logo-wrapper">
            <ConnectorLogo type="b3os" name="B3OS" logoUrl="https://cdn.b3.fun/b3os-logomark-dark-app-icon.svg" size="md" />
          </div>
          {selectedConnectorType && <>
              <ConnectorFlowAnimation />
              <div className="ds-logo-wrapper">
                <ConnectorLogo type={selectedConnectorType.type} name={selectedConnectorType.name} logoUrl={selectedConnectorType.logoUrl} size="md" />
              </div>
            </>}
        </div>

        {/* Title + description */}
        <div className="flex flex-col items-center gap-0.5 px-6 pt-4 text-center">
          <h2 className="text-ds-text-primary text-base font-medium">{modalTitle}</h2>
          {selectedConnectorType?.description && <p className="text-ds-text-muted text-sm leading-5">{selectedConnectorType.description}</p>}
        </div>

        {/* Spacer */}
        <div className="h-5" />

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-1 flex-col">
          <div className="flex flex-1 flex-col gap-5 px-6">
            {/* Connector Type (only show dropdown if not preselected and not editing) */}
            {!isEditing && !hasPreselectedType && <div>
                <label className="text-ds-text-secondary mb-1.5 block text-sm font-medium">
                  Connector Type <span className="text-ds-brand-600">*</span>
                </label>
                <Select value={selectedType || "__empty__"} onValueChange={v => handleTypeChange(v === "__empty__" ? "" : v)}>
                  <SelectTrigger className="rounded-ds-sm shadow-ds-soft border-ds-border-default bg-ds-surface-primary dark:bg-ds-neutral-800 h-10 w-full border">
                    <SelectValue placeholder="Select a connector type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__empty__">Select a connector type</SelectItem>
                    {connectorTypes.map(type => <SelectItem key={type.type} value={type.type}>
                        {type.name}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </div>}

            {/* Farcaster OAuth Flow */}
            {isFarcasterType && !isEditing && <div className="space-y-4 rounded-lg border border-purple-200 bg-purple-50 p-4 dark:border-purple-700 dark:bg-purple-900/20">
                <h3 className="body-sm-semibold">Connect Farcaster Account</h3>
                <FarcasterConnectButton onConnect={handleFarcasterConnect} onDisconnect={handleFarcasterDisconnect} />
                {data.fid && data.signerUuid && <div className="mt-3 rounded border border-green-300 bg-green-50 p-3 dark:border-green-700 dark:bg-green-900/20">
                    <p className="text-xs text-green-700 dark:text-green-300">
                      FID: {data.fid} | Signer UUID: {data.signerUuid.slice(0, 8)}...
                    </p>
                  </div>}
              </div>}

            {/* Pipedream OAuth Flow */}
            {isPipedreamType && !isEditing && <div className="space-y-3">
                {data.accountId ? <div className="flex items-center justify-between gap-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3 dark:border-green-800 dark:bg-green-900/20">
                    <div className="flex min-w-0 items-center gap-2 text-sm text-green-800 dark:text-green-200">
                      <IconCheckOutline18 className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">Connected via Pipedream</span>
                    </div>
                    <button type="button" onClick={handlePipedreamDisconnect} className="text-xs font-medium text-green-700 transition-colors hover:text-green-900 dark:text-green-300 dark:hover:text-green-100">
                      Use different account
                    </button>
                  </div> : null}
                {pipedreamError ? <p className="text-sm text-red-600 dark:text-red-400">{pipedreamError}</p> : null}
              </div>}

            {/* Setup Guide (if available) */}
            {selectedConnectorType?.setupGuide && !isEditing && <Accordion type="single" collapsible defaultValue="setup-guide" className="w-full">
                <AccordionItem value="setup-guide" className="border-green-200 dark:border-green-800">
                  <AccordionTrigger className="text-ds-text-primary text-sm font-semibold hover:no-underline">
                    <span className="flex items-center gap-2">
                      <IconBookOpenOutline18 className="h-4 w-4 text-green-600 dark:text-green-400" />
                      Setup Instructions
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="prose prose-sm dark:prose-invert max-w-none rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
                      <MemoizedMarkdown content={selectedConnectorType.setupGuide} id={`setup-guide-${selectedConnectorType.type}`} />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>}

            {/* SMS Phone Number — rendered before Name so the label auto-fills */}
            {selectedType === "sms" && dataSchema?.properties && Object.entries(dataSchema.properties).filter(([, fs]: [string, SchemaProperty]) => !fs.uiHidden && fs.format === "sms-number").map(([fieldName_0, fieldSchema]: [string, SchemaProperty]) => <SmsPhoneVerifyInput key={fieldName_0} label={fieldSchema.title || fieldName_0} description={fieldSchema.description} value={data[fieldName_0] || ""} onChange={val => handleFieldChange(fieldName_0, val)} onPhoneChange={handleSmsPhoneTyping} required={dataSchema.required?.includes(fieldName_0)} />)}

            {showTelegramModeSelector && <div>
                <label className="text-ds-text-secondary mb-1.5 block text-sm font-medium">
                  Telegram Bot <span className="text-ds-brand-600">*</span>
                </label>
                <RadioGroup value={isB3osBot ? "b3os" : "custom"} onValueChange={value_0 => handleSelectTelegramMode(value_0 as TelegramMode)} className="divide-ds-neutral-200 dark:divide-ds-neutral-800 bg-ds-surface-card gap-0 divide-y overflow-hidden">
                  <ConnectorOptionRow optionId="telegram-mode-b3os" value="b3os" title={TELEGRAM_DEFAULT_NAMES.b3os} subtitle="Quick setup, no token required" isSelected={isB3osBot} connectorType="telegram-bot" logoName={TELEGRAM_DEFAULT_NAMES.b3os} logoUrl={selectedConnectorType?.logoUrl} />
                  <ConnectorOptionRow optionId="telegram-mode-custom" value="custom" title="Your own bot" subtitle="Advanced: connect via @BotFather" isSelected={!isB3osBot} connectorType="telegram-bot" logoName="Custom Telegram Bot" logoUrl={selectedConnectorType?.logoUrl} />
                </RadioGroup>
              </div>}

            {/* Name */}
            <div>
              <label className="text-ds-text-secondary mb-1.5 block text-sm font-medium">
                Name <span className="text-ds-brand-600">*</span>
              </label>
              <Input type="text" value={name} onChange={e_0 => {
              setName(e_0.target.value);
              setNameManuallyEdited(true);
            }} placeholder={`My ${selectedConnectorType?.name || ""} Connector`} className="rounded-ds-sm shadow-ds-soft border-ds-border-default focus:border-ds-brand-500 focus:ring-ds-brand-500 w-full px-3 py-2 focus:ring-1" required />
            </div>

            {/* Native OAuth (Slack) button is pinned to the footer below. */}

            {/* Dynamic Data Fields */}
            {dataSchema?.properties && !isB3osBot && !isPushoverType && (!isFarcasterType && !isPipedreamType && !isNativeOAuthType || isEditing) && Object.entries(dataSchema.properties).filter(([, fs_0]: [string, SchemaProperty]) => !fs_0.uiHidden).filter(([, fs_1]: [string, SchemaProperty]) => !(selectedType === "sms" && fs_1.format === "sms-number")).map(([fieldName_1, fieldSchema_0]: [string, SchemaProperty]) => {
            const isRequired = dataSchema.required?.includes(fieldName_1);
            if (fieldSchema_0.format === "sms-number") {
              return <SmsPhoneVerifyInput key={fieldName_1} label={fieldSchema_0.title || fieldName_1} description={fieldSchema_0.description} value={data[fieldName_1] || ""} onChange={val_0 => handleFieldChange(fieldName_1, val_0)} onPhoneChange={handleSmsPhoneTyping} required={isRequired} />;
            }

            // Enum fields → Select dropdown
            if (fieldSchema_0.enum && fieldSchema_0.enum.length > 0) {
              return <div key={fieldName_1}>
                        <label className="text-ds-text-secondary mb-1.5 block text-sm font-medium">
                          {fieldSchema_0.title || fieldName_1}
                          {isRequired && <span className="text-ds-brand-600 ml-0.5">*</span>}
                        </label>
                        <Select value={data[fieldName_1] || fieldSchema_0.default || ""} onValueChange={val_1 => handleFieldChange(fieldName_1, val_1)}>
                          <SelectTrigger className="rounded-ds-sm shadow-ds-soft border-ds-border-default focus:border-ds-brand-500 focus:ring-ds-brand-500 w-full px-3 py-2 focus:ring-1">
                            <SelectValue placeholder={`Select ${fieldSchema_0.title || fieldName_1}`} />
                          </SelectTrigger>
                          <SelectContent>
                            {fieldSchema_0.enum.map(option => <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>)}
                          </SelectContent>
                        </Select>
                        {fieldSchema_0.description && <p className="text-ds-text-muted leading-18 mt-1.5 text-xs">{fieldSchema_0.description}</p>}
                      </div>;
            }

            // Multiline password fields (e.g., PEM keys) → Textarea
            const isMultilineSecret = fieldSchema_0.format === "password" && (fieldName_1.toLowerCase().includes("private") || fieldName_1.toLowerCase().includes("pem") || fieldName_1.toLowerCase().includes("certificate"));
            return <div key={fieldName_1}>
                      <label className="text-ds-text-secondary mb-1.5 block text-sm font-medium">
                        {fieldSchema_0.title || fieldName_1}
                        {isRequired && <span className="text-ds-brand-600 ml-0.5">*</span>}
                      </label>
                      {isMultilineSecret ? <Textarea value={data[fieldName_1] || ""} onChange={e_1 => handleFieldChange(fieldName_1, e_1.target.value)} placeholder="Paste full key content including BEGIN/END lines" rows={6} className="rounded-ds-sm shadow-ds-soft border-ds-border-default focus:border-ds-brand-500 focus:ring-ds-brand-500 w-full resize-y px-3 py-3 font-mono text-xs focus:ring-1" required={isRequired} /> : <Input type={fieldSchema_0.format === "password" ? "password" : fieldSchema_0.format === "uri" ? "url" : "text"} value={data[fieldName_1] || ""} onChange={e_2 => handleFieldChange(fieldName_1, e_2.target.value)} placeholder={fieldSchema_0.pattern ? `e.g., ${fieldSchema_0.pattern}` : ""} className="rounded-ds-sm shadow-ds-soft border-ds-border-default focus:border-ds-brand-500 focus:ring-ds-brand-500 w-full px-3 py-2 focus:ring-1" required={isRequired} />}
                      {fieldSchema_0.description && <p className="text-ds-text-muted leading-18 mt-1.5 text-xs">{fieldSchema_0.description}</p>}
                    </div>;
          })}

            {/* Description — shown for all connector types (patched post-OAuth for Slack). */}
            <div>
              <label className="text-ds-text-secondary mb-1.5 block text-sm font-medium">Description</label>
              <Textarea value={description} onChange={e_3 => setDescription(e_3.target.value)} placeholder="Enter a description" rows={6} className="rounded-ds-sm shadow-ds-soft border-ds-border-default focus:border-ds-brand-500 focus:ring-ds-brand-500 w-full resize-y px-3 py-3 focus:ring-1" />
            </div>
          </div>

          {/* Footer actions */}
          <div className="mt-8 flex flex-col">
            <div className="bg-ds-neutral-200 dark:bg-ds-neutral-700 h-px" />
            <div className="flex items-center px-6 pb-6 pt-4">
              {/* Documentation button */}
              {selectedConnectorType?.informationUrl && <a href={selectedConnectorType.informationUrl} target="_blank" rel="noopener noreferrer" className="hover:to-zinc-150 rounded-ds-sm shadow-ds-white-glossy from-ds-neutral-100 to-ds-neutral-200 text-ds-text-primary dark:from-ds-neutral-700 dark:to-ds-neutral-800 flex h-10 items-center gap-1 border border-black/10 bg-gradient-to-b px-4 text-sm font-medium transition-all hover:from-zinc-50 active:scale-[0.98] dark:text-zinc-200">
                  <IconFileText className="size-4" />
                  Documentation
                </a>}

              {/* Right-side actions */}
              <div className="ml-auto flex items-center gap-3">
                {/* Native OAuth (Slack) — pinned primary action for the bottom of the modal */}
                {isNativeOAuthType && !isEditing && <SlackOAuthConnectButton disabled={!hasConnectorName} connectorName={name.trim()} onSuccess={async () => {
                const before_0 = useConnectorsStore.getState().connectors;
                await loadConnectors();
                // Patch the newly-created connector with the description
                // field, since the Slack OAuth callback creates the connector
                // with only the name and doesn't receive the description.
                await patchNewConnectorWithDescription(before_0);
                onClose();
              }} onPopupClosed={async () => {
                const before_1 = useConnectorsStore.getState().connectors;
                await loadConnectors();
                const after_0 = useConnectorsStore.getState().connectors;
                if (after_0.length > before_1.length) {
                  await patchNewConnectorWithDescription(before_1);
                  showToast.success("Slack workspace connected");
                  onClose();
                }
              }} />}
                {!isEditing && isPipedreamType && !data.accountId ? <button type="button" onClick={handlePipedreamFooterConnect} disabled={isPipedreamConnecting || !hasConnectorName} className={PRIMARY_GRADIENT_BUTTON_CLASS}>
                    {isPipedreamConnecting ? <>
                        <PixelLoader size="sm" />
                        Connecting...
                      </> : <>
                        <IconChevronRightOutline18 className="size-4" />
                        Connect
                      </>}
                  </button> : null}
                {showSubmitButton ? <button type="submit" disabled={isSubmitting || !hasConnectorName || !hasRequiredFields} className={PRIMARY_GRADIENT_BUTTON_CLASS}>
                    {isSubmitting ? <>
                        <PixelLoader size="sm" />
                        {isEditing ? "Updating..." : "Connecting..."}
                      </> : <>{isEditing ? "Update" : "Connect"}</>}
                  </button> : null}
              </div>
            </div>
          </div>
        </form>
      </div>
    </ResponsiveModal>;
}

/**
 * Two 5×3 pixel arrows — one pointing right, one pointing left — offset
 * diagonally over a faint background grid. The right-arrow sits in the
 * upper-right quadrant, the left-arrow in the lower-left, evoking
 * bidirectional data flow between B3OS and the connector.
 *
 * Within each arrow the chase fires from base → tip: base cells (step 0)
 * fire first, the tip cell (step 2) fires last, so the pulse streams
 * outward in the direction the arrow points.
 */
type LitPixel = readonly [row: number, col: number, delayStep: number];
const PIXEL_COLS = 7;
const PIXEL_ROWS = 8;
const PIXEL_CELL_PX = 3;
const PIXEL_GAP_PX = 2;
const PIXEL_DELAY_MS = 140;

// Right-arrow (→) anchored at rows 0-4, cols 4-6 (upper-right).
// Left-arrow (←) anchored at rows 3-7, cols 0-2 (lower-left).
const LIT_PIXELS: readonly LitPixel[] = [
// Right-arrow base (col 4, 5 cells tall)
[0, 4, 0], [1, 4, 0], [2, 4, 0], [3, 4, 0], [4, 4, 0],
// Right-arrow middle (col 5, 3 cells)
[1, 5, 1], [2, 5, 1], [3, 5, 1],
// Right-arrow tip (col 6)
[2, 6, 2],
// Left-arrow base (col 2, 5 cells tall)
[3, 2, 0], [4, 2, 0], [5, 2, 0], [6, 2, 0], [7, 2, 0],
// Left-arrow middle (col 1, 3 cells)
[4, 1, 1], [5, 1, 1], [6, 1, 1],
// Left-arrow tip (col 0)
[5, 0, 2]];
function ConnectorFlowAnimation() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div aria-hidden={true} className="text-ds-brand-500 grid" style={{
      gridTemplateColumns: `repeat(${PIXEL_COLS}, ${PIXEL_CELL_PX}px)`,
      gridTemplateRows: `repeat(${PIXEL_ROWS}, ${PIXEL_CELL_PX}px)`,
      gap: `${PIXEL_GAP_PX}px`
    }}>{LIT_PIXELS.map(_temp)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp(t0) {
  const [r, c, step] = t0;
  return <span key={`${r}-${c}`} className="animate-pixel-chase block h-full w-full bg-current" style={{
    gridRow: r + 1,
    gridColumn: c + 1,
    animationDelay: `${step * PIXEL_DELAY_MS}ms`
  }} />;
}